import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import BrandLockup from '../components/BrandLockup';
import { accountVerificationApi } from '../api/accountVerificationApi';
import { useAuth } from '../auth/useAuth';
import { useProfile } from '../context/ProfileContext';

type ConfirmationState = 'confirming' | 'confirmed' | 'failed';

const ConfirmEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, loginClubhouseHandoff } = useAuth();
  const { refreshVerification } = useProfile();
  const [state, setState] = useState<ConfirmationState>('confirming');
  const [isContinuing, setIsContinuing] = useState(false);
  const [continueError, setContinueError] = useState<string | null>(null);
  // Handoff returned by the confirm call; exchanged for a session so clicking the emailed link is
  // enough to get in. Non-null means a sign-in is still pending, which gates the onboarding hop.
  const [signInToken, setSignInToken] = useState<string | null>(null);

  // Grab the single-use token from the URL fragment once, in a state initializer, so it survives the
  // effect stripping it from the address bar and StrictMode's double mount in development.
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token'));

  const continueStarted = useRef(false);
  const goToOnboarding = useCallback(async () => {
    if (isLoading || signInToken || continueStarted.current) return;
    if (!isAuthenticated) {
      navigate('/login?returnTo=/onboarding', { replace: true });
      return;
    }

    continueStarted.current = true;
    setIsContinuing(true);
    setContinueError(null);
    try {
      const verification = await refreshVerification();
      if (!verification?.isVerified) {
        setContinueError('Your email was confirmed, but your signed-in account has not updated yet. Try again.');
        return;
      }
      navigate('/onboarding', { replace: true });
    } catch (error) {
      // A session the API no longer recognizes (deleted account, stale token) can never refresh,
      // so send them to sign in rather than leaving them on a retry that always fails.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401 || status === 404) {
        navigate('/login?returnTo=/onboarding', { replace: true });
        return;
      }
      setContinueError('Your email was confirmed, but we could not refresh your account. Try again.');
    } finally {
      continueStarted.current = false;
      setIsContinuing(false);
    }
  }, [isAuthenticated, isLoading, navigate, refreshVerification, signInToken]);

  // Consume the token once (anonymous POST). The ref guard keeps this to a single request under
  // StrictMode's double-invoked effects, so the one-shot token is never spent twice and reported dead.
  const confirmStarted = useRef(false);
  useEffect(() => {
    window.history.replaceState(null, '', '/confirm-email');
    if (!token) {
      setState('failed');
      return;
    }
    if (confirmStarted.current) return;
    confirmStarted.current = true;
    accountVerificationApi.confirm(token)
      .then((result) => {
        setSignInToken(result.signInToken ?? null);
        setState('confirmed');
      })
      .catch(() => setState('failed'));
  }, [token]);

  // Whoever opened the emailed link owns the account being confirmed, so start their session from
  // the handoff it returned. This also displaces a stale or different session left on this device,
  // which would otherwise fail the verification refresh below.
  const signInStarted = useRef(false);
  useEffect(() => {
    if (state !== 'confirmed' || !signInToken || isLoading || signInStarted.current) return;
    signInStarted.current = true;
    loginClubhouseHandoff(signInToken)
      .catch(() => undefined)
      .finally(() => setSignInToken(null));
  }, [state, signInToken, isLoading, loginClubhouseHandoff]);

  // Once confirmed and auth has settled, sync verification and send them to onboarding.
  useEffect(() => {
    if (state !== 'confirmed' || isLoading || signInToken) return;
    void goToOnboarding();
  }, [state, isLoading, signInToken, goToOnboarding]);

  return (
    <div className="onboarding-page">
      <div className="onboarding-page__scene" aria-hidden="true" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/onboarding-bg.jpg)` }} />
      <div className="onboarding-page__shade" aria-hidden="true" />
      <section className="onboarding-card onboarding-card--narrow">
        <div className="onboarding-card__brand"><BrandLockup tone="onLight" compact /></div>
        <div className="onboarding-card__header">
          <h1>{state === 'confirming' ? 'Confirming your email' : state === 'confirmed' ? 'Email confirmed' : 'Link unavailable'}</h1>
          <p>{state === 'confirming'
            ? 'One moment while we confirm your CaddieSight account.'
            : state === 'confirmed'
              ? 'Your email is confirmed — taking you to complete your profile and choose a membership.'
              : 'This confirmation link is invalid or has expired. Sign in to request a new one.'}</p>
        </div>
        {state === 'confirming' ? <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} aria-label="Confirming email" /> : null}
        {continueError ? <div className="onboarding-note onboarding-note--error" role="alert">{continueError}</div> : null}
        {state === 'confirmed' ? (
          <Button
            label="Continue to setup"
            icon="pi pi-arrow-right"
            loading={isContinuing || Boolean(signInToken)}
            disabled={isLoading}
            onClick={() => { void goToOnboarding(); }}
          />
        ) : null}
        {state === 'failed' ? (
          <Button label="Sign in" icon="pi pi-sign-in" onClick={() => navigate('/login', { replace: true })} />
        ) : null}
      </section>
    </div>
  );
};

export default ConfirmEmailPage;